import { useMemo } from 'react'
import { useSealedProductPriceHistory } from '@/hooks/products/useSealedProducts'
import type { PriceHistory } from '@/types'

/**
 * Chart-ready price history for a sealed product. The sealed prices endpoint
 * doesn't track an inventory (low) price, only market price — this reshapes
 * the raw `{ marketPrice, recordedAt }` rows into full `PriceHistory` entries
 * (with `inventoryPrice: null`) so `<PriceChart />` can consume them directly,
 * keeping the reshaping out of the page component.
 */
export function useSealedProductPriceChart(id: number | undefined, days: number = 30) {
  const { data, isLoading } = useSealedProductPriceHistory(id, days)

  const priceHistory = useMemo<PriceHistory[]>(
    () =>
      (data?.prices ?? []).map((p) => ({
        marketPrice: p.marketPrice,
        inventoryPrice: null,
        recordedAt: p.recordedAt,
      })),
    [data],
  )

  return { priceHistory, isLoading }
}
